import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import API from "../api/client";
import * as ENDPOINTS from "../api/endpoints";
import { CustomerData } from "../components/utils/customers/CustomerData";

const initialState = {
  cartData: [],
  cartItems: CustomerData.getCartitems() || [],
  status: null,
  error: null,
  loading: false,
};

export const cartFetch = createAsyncThunk(
  "cart/cartFetch",
  async (customerId = null, { rejectWithValue }) => {
    try {
      if (!customerId) {
        const customer = CustomerData.getCustomer();
        customerId = customer._id;
      }

      if (!customerId) {
        return;
      }

      const response = await API.getWithToken(
        `${ENDPOINTS.CART}/${customerId}`
      );
      if (response?.data?.data?.cart?.items) {
        const items = response?.data?.data?.cart.items;
        let payloadItems = [];
        items.forEach((item) => {
          const it = {
            product_id: item.product_id?._id,
            customer_id: customerId,
            qty: item.quantity,
            name: item.product_name,
          };
          payloadItems.push(it);
        });
        CustomerData.setCartItems(payloadItems);
      }

      return response?.data?.data?.cart;
    } catch (error) {
      console.log("error", error);
      return rejectWithValue(error.response.data);
    }
  }
);

// Single API call to add multiple free gifts in one request.
export const addFreeToCart = createAsyncThunk(
  "cart/addFreeToCart",
  async (payload, { rejectWithValue }) => {
    try {
      const { cartId, freeGifts } = payload;

      // Get customer ID from stored customer data
      const customer = CustomerData.getCustomer();
      const customerId = customer._id;

      // Build items payload for single request
      const items = (freeGifts || []).map((gift) => ({
        cart_id: cartId,
        product_id: gift.product?._id || gift.product?.id || gift.product_id,
        product_code: gift.product?.code || gift.code,
        product_name: gift.product?.name || gift.name,
        quantity: gift.quantity || 1,
        price: 0,
        type: "free",
        is_free_gift: true,
      }));

      console.log("Sending free gifts to backend:", { customer_id: customerId, items });

      // Post all free items in one call to a dedicated endpoint with customer_id
      const postResponse = await API.postWithToken(ENDPOINTS.ADD_FREE_TO_CART, { 
        customer_id: customerId,
        items 
      });
      
      console.log("Free gifts post response:", postResponse?.data);

      // Fetch updated cart after adding free gifts with a small delay to ensure DB is updated
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const response = await API.getWithToken(`${ENDPOINTS.CART}/${customerId}`);
      console.log("Updated cart after adding free gifts:", response?.data?.data?.cart);
      
      return response?.data?.data?.cart;
    } catch (error) {
      console.error("error adding free gifts (single call)", error);
      return rejectWithValue(error.response?.data);
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCart(state, action) {
      state.cartItems = [];
      state.cartData = [];
      CustomerData.clearCartData();
    },
    addToCart(state, action) {
      const { name, qty, translations, shipping } = action.payload;
      const res = API.postWithToken(ENDPOINTS.ADD_TO_CART, action.payload);
      let message = translations.added_to_cart;
      state.loading = true;

      const itemIndex = state.cartItems.findIndex(
        (item) => item.product_id === action.payload.product_id
      );
      if (itemIndex >= 0) {
        state.cartItems[itemIndex].qty = action.payload.qty;
        message =
          qty == 0
            ? translations.removed_from_cart
            : translations.qty_updated_cart;
      } else {
        state.cartItems.push(action.payload);
        message = translations.added_to_cart;
      }

      res
        .then((response) => {
          toast.success(`${name} ${message}.`, {
            position: "bottom-left",
          });
        })
        .catch((err) => console.log(err));

      CustomerData.setCartItems(state.cartItems);
    },

    removeFromCart(state, action) {
      const { translations } = action.payload;
      const res = API.deleteWithToken(
        `${ENDPOINTS.CART_DELETE_ITEM}/${action.payload.cart_id}/${action.payload.item_id}`
      );
      state.loading = true;
      res
        .then((response) => {
          toast.success(
            `${action.payload.name} ${translations.removed_from_cart}`,
            {
              position: "bottom-left",
            }
          );
        })
        .catch((err) => console.log(err));

      const otherItems = state.cartItems.filter(
        (cartItem) => cartItem.product_id !== action.payload.product_id
      );

      state.cartItems = otherItems;

      CustomerData.setCartItems(state.cartItems);
    },

    updateCart(state, action) {
      const { translations } = action.payload;
      state.loading = true;
      console.log('start update')
      const res = API.putWithToken(
        `${ENDPOINTS.CART_DELETE_ITEM}/${action.payload.cart_id}/${action.payload.item_id}`,
        { qty: action.payload.qty }
      );       
      const message =
        action.payload.qty > 0
          ? translations.qty_updated_cart
          : translations.removed_from_cart;
      
      res
        .then((response) => {
          toast.success(`${action.payload.name} ${message}`, {
            position: "bottom-left",
          });
        })
        .catch((err) => console.log(err));
    },
    repeatOrder(state, action) {
      const { translations } = action.payload;
      const res = API.postWithToken(ENDPOINTS.REPEAT_ORDER, action.payload);
      state.loading = true;
      res
        .then((response) => {
          if (response.data.status == "success") {
            toast.success(translations.items_added_to_cart, {
              position: "bottom-left",
            });
          } else {
            toast.error(response.data.message);
          }
        })
        .catch((err) => console.log(err));
    },

    clearCartData(state, action) {
      const { translations } = action.payload;
      const res = API.deleteWithToken(
        `${ENDPOINTS.CLEAR_CART}/${action.payload.cart_id}`
      );
      state.loading = true;
      res
        .then((response) => {
          toast.success(
            `${translations.cart_cleared}`,
            {
              position: "bottom-left",
            }
          );
        })
        .catch((err) => console.log(err));

      // const otherItems = state.cartItems.filter(
      //   (cartItem) => cartItem.product_id !== action.payload.product_id
      // );

      // state.cartItems = otherItems;

      // CustomerData.setCartItems(state.cartItems);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cartFetch.pending, (state, action) => {
        state.loading = true;
        state.status = "pending";
      })
      .addCase(cartFetch.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "success";
        //console.log('action.payload', action.payload);
        state.cartData = action.payload;
      })
      .addCase(cartFetch.rejected, (state, action) => {
        state.loading = false;
        state.status = "rejected";
        state.error = action.payload;
      })
      .addCase(addFreeToCart.pending, (state, action) => {
        state.loading = true;
        state.status = "pending";
      })
      .addCase(addFreeToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "success";
        state.cartData = action.payload;
      })
      .addCase(addFreeToCart.rejected, (state, action) => {
        state.loading = false;
        state.status = "rejected";
        state.error = action.payload;
      });
  },


});

export const { addToCart, removeFromCart, updateCart, repeatOrder, clearCart, clearCartData } =
  cartSlice.actions;

export default cartSlice.reducer;
